import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { apiService } from '../../services/apiService';

const Section: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({ title, description, children }) => (
  <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
    <Typography variant="h6" gutterBottom>{title}</Typography>
    {description && (
      <Typography variant="body2" color="text.secondary" gutterBottom>{description}</Typography>
    )}
    {children}
  </Paper>
);

const CeleryQueuesAdmin: React.FC = () => {
  const { keycloak, initialized } = useAuth();
  const navigate = useNavigate();

  const isAdministrator = useMemo(() => {
    try {
      const token: any = keycloak?.tokenParsed || {};
      const groups: string[] = (token?.groups || []) as string[];
      const ADMIN_NAME = (process.env.REACT_APP_ADMIN_GROUP_NAME || 'administrator');
      return Array.isArray(groups) && groups.some(g => String(g).replace(/^\//, '') === ADMIN_NAME);
    } catch {
      return false;
    }
  }, [keycloak]);

  useEffect(() => {
    if (keycloak?.token) {
      apiService.setToken(keycloak.token);
    }
  }, [keycloak]);

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const resp = await apiService.getCeleryWorkers();
        const items = resp.data?.items || [];
        setWorkers(items);
        if (!assignWorker && items.length > 0) {
          setAssignWorker(items[0].worker);
        }
      } catch (e: any) {
        // noop, surfaced per-action
      }
    };
    if (isAdministrator) {
      fetchWorkers();
    }
  }, [isAdministrator]);

  const [purgeQueuesInput, setPurgeQueuesInput] = useState<string>('');
  const [purgeConfirm, setPurgeConfirm] = useState<boolean>(false);
  const [purgeResult, setPurgeResult] = useState<any | null>(null);
  const [purgeLoading, setPurgeLoading] = useState<boolean>(false);
  const [purgeError, setPurgeError] = useState<string | null>(null);

  const [moveSource, setMoveSource] = useState<string>('celery');
  const [moveTarget, setMoveTarget] = useState<string>('ai');
  const [moveMax, setMoveMax] = useState<number>(10000);
  const [moveResult, setMoveResult] = useState<any | null>(null);
  const [moveLoading, setMoveLoading] = useState<boolean>(false);
  const [moveError, setMoveError] = useState<string | null>(null);

  // Assign to Worker state
  const [workers, setWorkers] = useState<any[]>([]);
  const [assignSource, setAssignSource] = useState<string>('ai');
  const [assignWorker, setAssignWorker] = useState<string>('');
  const [assignQueueName, setAssignQueueName] = useState<string>('');
  const [assignMax, setAssignMax] = useState<number>(1000);
  const [assignLoading, setAssignLoading] = useState<boolean>(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignResult, setAssignResult] = useState<any | null>(null);

  const handlePurge = async () => {
    setPurgeError(null); setPurgeResult(null); setPurgeLoading(true);
    try {
      const raw = purgeQueuesInput.split(',').map(q => q.trim()).filter(q => q.length > 0);
      if (raw.length === 0) {
        setPurgeError('Please enter at least one queue name.');
        setPurgeLoading(false);
        return;
      }
      if (!purgeConfirm) {
        setPurgeError('You must check the confirmation box to purge queues.');
        setPurgeLoading(false);
        return;
      }
      const resp = await apiService.purgeCeleryQueues({ queues: raw, confirm: true });
      setPurgeResult(resp.data);
    } catch (e: any) {
      setPurgeError(e?.response?.data?.detail || 'Failed to purge queues');
    } finally {
      setPurgeLoading(false);
    }
  };

  const handleMove = async () => {
    setMoveError(null); setMoveResult(null); setMoveLoading(true);
    try {
      if (!moveSource || !moveTarget) {
        setMoveError('Source and target queues are required.');
        setMoveLoading(false);
        return;
      }
      if (moveSource === moveTarget) {
        setMoveError('Source and target must be different.');
        setMoveLoading(false);
        return;
      }
      const resp = await apiService.moveCeleryMessages({ source: moveSource.trim(), target: moveTarget.trim(), max: Number(moveMax) || 1 });
      setMoveResult(resp.data);
    } catch (e: any) {
      setMoveError(e?.response?.data?.detail || 'Failed to move messages');
    } finally {
      setMoveLoading(false);
    }
  };

  const handleAssign = async () => {
    setAssignError(null); setAssignResult(null); setAssignLoading(true);
    try {
      if (!assignSource || !assignWorker) {
        setAssignError('Source queue and worker are required.');
        setAssignLoading(false);
        return;
      }
      const payload: any = { source: assignSource.trim(), worker: assignWorker.trim(), max: Number(assignMax) || 1 };
      if (assignQueueName && assignQueueName.trim().length > 0) payload.queue_name = assignQueueName.trim();
      const resp = await apiService.assignCeleryMessages(payload);
      setAssignResult(resp.data);
    } catch (e: any) {
      setAssignError(e?.response?.data?.detail || 'Failed to assign messages to worker');
    } finally {
      setAssignLoading(false);
    }
  };

  if (!initialized) return <LoadingSpinner />;
  if (!isAdministrator) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Administrator Access Required</Typography>
        <Typography variant="body1" gutterBottom>You must be an administrator to manage Celery queues.</Typography>
        <Button variant="outlined" onClick={() => navigate('/admin')}>Back to Admin</Button>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Celery Queue Maintenance</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Use these tools to clear misrouted backlogs or move tasks between queues. Proceed with caution — purging permanently deletes pending tasks.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Section title="Purge Queues" description="Delete all pending messages from one or more Redis list queues (e.g., celery, ai, sync). This cannot be undone.">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Queue names (comma-separated)"
                placeholder="celery, ai, sync"
                value={purgeQueuesInput}
                onChange={e => setPurgeQueuesInput(e.target.value)}
                fullWidth
              />
              <FormControlLabel control={<Checkbox checked={purgeConfirm} onChange={(e) => setPurgeConfirm(e.target.checked)} />} label="I understand this will permanently delete pending tasks in the specified queues." />
              <Box>
                <Button variant="contained" color="error" onClick={handlePurge} disabled={purgeLoading || !purgeConfirm}>
                  {purgeLoading ? 'Purging…' : 'Purge Queues'}
                </Button>
              </Box>
              {purgeError && <Alert severity="error">{purgeError}</Alert>}
              {purgeResult && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Result</Typography>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(purgeResult, null, 2)}</pre>
                </Paper>
              )}
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Move Messages" description="Move up to N pending messages from a source queue to a target queue (e.g., from default 'celery' to 'ai').">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Source queue" value={moveSource} onChange={e => setMoveSource(e.target.value)} fullWidth />
              <TextField label="Target queue" value={moveTarget} onChange={e => setMoveTarget(e.target.value)} fullWidth />
              <TextField label="Max messages" type="number" value={moveMax} onChange={e => setMoveMax(Number(e.target.value))} fullWidth />
              <Box>
                <Button variant="contained" onClick={handleMove} disabled={moveLoading}>
                  {moveLoading ? 'Moving…' : 'Move Messages'}
                </Button>
              </Box>
              {moveError && <Alert severity="error">{moveError}</Alert>}
              {moveResult && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Result</Typography>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(moveResult, null, 2)}</pre>
                </Paper>
              )}
            </Box>
          </Section>
        </Grid>

        <Grid item xs={12} md={6}>
          <Section title="Assign Pending To Worker" description="For tasks with worker=null, assign up to N messages from a source queue to a specific worker. This creates or uses a worker-specific queue and asks that worker to consume it.">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Source queue" value={assignSource} onChange={e => setAssignSource(e.target.value)} fullWidth />
              <TextField
                select
                label="Worker"
                SelectProps={{
                  native: true,
                  onChange: (e: SelectChangeEvent<unknown>, _child: React.ReactNode) => setAssignWorker(e.target.value as string),
                }}
                value={assignWorker}
                fullWidth
              >
                <option value="" disabled>Select a worker…</option>
                {workers.map((w) => (
                  <option key={w.worker} value={w.worker}>{w.worker}</option>
                ))}
              </TextField>
              <TextField label="Optional target queue name" placeholder="(defaults to source.worker)" value={assignQueueName} onChange={e => setAssignQueueName(e.target.value)} fullWidth />
              <TextField label="Max messages" type="number" value={assignMax} onChange={e => setAssignMax(Number(e.target.value))} fullWidth />
              <Box>
                <Button variant="contained" onClick={handleAssign} disabled={assignLoading || !assignWorker}>
                  {assignLoading ? 'Assigning…' : 'Assign To Worker'}
                </Button>
              </Box>
              {assignError && <Alert severity="error">{assignError}</Alert>}
              {assignResult && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Result</Typography>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(assignResult, null, 2)}</pre>
                </Paper>
              )}
            </Box>
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CeleryQueuesAdmin;
